// Library blocks — named concepts COMPOSED FROM PRIMITIVES, reusable. This is
// the heart of the idea: "price above its average", "breakout", etc. are not
// special features — they're just named functions that emit a subgraph, exactly
// like a user's own saved block. Glass boxes, all the way down.

import type { GraphBuilder, NodeId } from "./graph.js";

/** Event: close is above its own SMA(period). */
export function priceAboveSma(b: GraphBuilder, period: number): NodeId {
  return b.gt(b.price("c"), b.sma(period, b.price("c")));
}

/** Event: close crosses above SMA(period) this bar. */
export function crossUpSma(b: GraphBuilder, period: number): NodeId {
  return b.crossAbove(b.price("c"), b.sma(period, b.price("c")));
}

/** Event: close crosses below SMA(period) this bar. */
export function crossDownSma(b: GraphBuilder, period: number): NodeId {
  return b.crossBelow(b.price("c"), b.sma(period, b.price("c")));
}

/** Event: breakout — close pushes above the highest high of the prior N bars.
 * A user could build "liquidity grab" the same way (sweep a level, then reclaim)
 * once we add swing-point / level primitives. */
export function breakoutHigh(b: GraphBuilder, period: number): NodeId {
  return b.crossAbove(b.price("c"), b.highest(period, b.price("h")));
}

/** Event: on a HIGHER timeframe, price is above its SMA — a trend filter. Held
 * to the base bars by the closed-bar rule. `tfMinutes` e.g. 240 (4H), 60 (1H). */
export function htfUptrend(b: GraphBuilder, tfMinutes: number, period: number): NodeId {
  return b.onTimeframe(tfMinutes, (h) => h.gt(h.price("c"), h.sma(period, h.price("c"))));
}

/** The recent support level = the lowest low of the prior `lookback` bars (shifted
 * one bar so the current bar doesn't define its own level). A Series a user would
 * see as a line on the chart. */
export function supportLevel(b: GraphBuilder, lookback: number): NodeId {
  return b.shift(1, b.lowest(lookback, b.price("l")));
}

/** Event: BULLISH LIQUIDITY GRAB — price sweeps below recent support (stops run)
 * then reclaims it on the same bar (close back above). Composed entirely from
 * primitives: lowest + shift + compares. A user could build/name this themselves;
 * we just ship it as a glass-box library block. */
export function liquidityGrab(b: GraphBuilder, lookback: number): NodeId {
  const support = supportLevel(b, lookback);
  const swept = b.lt(b.price("l"), support);   // low dipped below support
  const reclaimed = b.gt(b.price("c"), support); // close back above it
  return b.and(swept, reclaimed);
}

// --- Structure / levels (from swing points) ---------------------------------
export function swingSupport(b: GraphBuilder, span: number): NodeId { return b.swingLowLevel(span, b.price("l")); }
export function swingResistance(b: GraphBuilder, span: number): NodeId { return b.swingHighLevel(span, b.price("h")); }

/** Event: price is AT a swing-support level and holding (low within `tolPct` of
 * the level, closing green above it) — a support bounce. */
export function atSupport(b: GraphBuilder, span: number, tolPct: number): NodeId {
  const lvl = swingSupport(b, span);
  const near = b.lt(b.price("l"), b.mul(lvl, b.const(1 + tolPct / 100)));
  const above = b.gt(b.price("c"), lvl);
  const green = b.gt(b.price("c"), b.price("o"));
  return b.and(b.and(near, above), green);
}

/** Event: BREAK OF STRUCTURE up — close breaks above the last swing high. */
export function breakStructureUp(b: GraphBuilder, span: number): NodeId {
  return b.crossAbove(b.price("c"), swingResistance(b, span));
}

/** Event: bullish REJECTION WICK (hammer-ish) — close in the top of the bar's
 * range with a long lower wick, and green. Composed from OHLC + a ratio. */
export function rejectionWickBull(b: GraphBuilder): NodeId {
  const range = b.sub(b.price("h"), b.price("l"));
  const fromLow = b.sub(b.price("c"), b.price("l"));
  const topHalf = b.gt(fromLow, b.mul(range, b.const(0.6)));
  const green = b.gt(b.price("c"), b.price("o"));
  return b.and(topHalf, green);
}

/** Event: close is above a rising trendline drawn through the last two swing
 * lows — i.e. the up-trendline is intact. */
export function aboveTrendlineUp(b: GraphBuilder, span: number): NodeId {
  return b.gt(b.price("c"), b.trendlineLow(span));
}

/** Event: breakout of the low of the prior N bars (a short trigger). */
export function breakoutLow(b: GraphBuilder, lookback: number): NodeId {
  return b.crossBelow(b.price("c"), b.lowest(lookback, b.price("l")));
}

/** Event: bullish engulfing — a green bar that engulfs the prior red bar. */
export function bullishEngulfing(b: GraphBuilder): NodeId {
  const c = b.price("c"), o = b.price("o"), pc = b.shift(1, c), po = b.shift(1, o);
  const greenNow = b.gt(c, o), redPrev = b.lt(pc, po);
  const engulf = b.and(b.gt(c, po), b.lt(o, pc));
  return b.and(b.and(greenNow, redPrev), engulf);
}

/** Event: volume spike — volume above `mult`× its own average. */
export function volumeSpike(b: GraphBuilder, period: number, mult: number): NodeId {
  return b.gt(b.price("v"), b.mul(b.sma(period, b.price("v")), b.const(mult)));
}
